import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import BottomDrawer from '../../../common/_custom/BottomDrawer/BottomDrawer';
import { useAppSelector } from '../../../app/hooks';
import { useAicaReportV2Logic } from '../useAicaReportV2Logic';
import { ReportDocument } from './ReportDocument';
import { ReportPreview } from './ReportPreview';
import { ModuleRail } from './ModuleRail';
import { LayoutSettingsPopover } from './LayoutSettingsPopover';
import type { PdfLayoutOverrides } from '../tokens/pdfLayout';
import { logPdfFailureDiagnostic } from './pdfFailureDiagnostic';
import { useVisibilityConfig } from '../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../utils/constants/visibilityKeys';
import styles from './AicaReportV2Drawer.module.scss';

export type AicaReportV2DrawerProps = {
    assesseeOrgId?: string;
    orgName?: string;
    onClose: () => void;
};

const AicaReportV2Drawer: React.FC<AicaReportV2DrawerProps> = ({ assesseeOrgId, orgName, onClose }) => {
    const logic = useAicaReportV2Logic(assesseeOrgId);
    const [downloadState, setDownloadState] = useState<'idle' | 'preparing' | 'error'>('idle');
    // Render the PNO (Partner-Network-Org) logo on the cover. The drawer is
    // the right place to read the active PNO context out of Redux — the
    // ReportDocument is a react-pdf tree and can't use hooks itself, so
    // we hand the URL down as a prop.
    const pnoLogoUrl = useAppSelector((state: any) => state.user?.domainMetadata?.logoUrl as string | undefined);

    // Opt-in layout overrides for testing alternative paper sizes / paddings /
    // module gaps. Empty by default so production behaviour is unchanged. The
    // popover writes here; ReportDocument + ReportPreview consume it. The
    // state lives in this top-level component (not Redux) because the
    // feature is scoped to a single drawer session and shouldn't persist.
    const [layoutOverrides, setLayoutOverrides] = useState<PdfLayoutOverrides>({});

    // Layout-popover visibility is config-driven. Levels:
    //   missing key  → hide (no popover, no button)
    //   -1 (HIDDEN)  → hide
    //    0 (READ)    → show button, disabled (can view but can't change)
    //    1 (WRITE)   → show + enabled
    // Default is HIDE on missing — the visibility helper's READ default is
    // wrong for this control because we don't want to ship the layout
    // popover to envs that haven't explicitly opted in.
    const { config: visibilityConfig, getFeatureVisibility } = useVisibilityConfig();
    const layoutKey = VISIBILITY_KEYS.AICA_REPORT.CUSTOM_LAYOUT;
    const layoutKeyPresent = !!visibilityConfig && Object.prototype.hasOwnProperty.call(visibilityConfig, layoutKey);
    const layoutVis = getFeatureVisibility(layoutKey);
    const showLayoutPopover = layoutKeyPresent && !layoutVis.hidden;
    const layoutPopoverDisabled = layoutVis.disabled;

    const docProps = useMemo(
        () => ({
            data: logic.data,
            enabledModules: logic.enabledModules,
            pnoLogoUrl,
            layoutOverrides,
        }),
        [logic.data, logic.enabledModules, pnoLogoUrl, layoutOverrides],
    );

    const fileName = useMemo(() => {
        const name = (logic.data.identity.orgName || orgName || 'report').replace(/[^a-z0-9]+/gi, '_');
        const date = new Date().toISOString().slice(0, 10);
        return `${name}_AICA_Report_${date}.pdf`;
    }, [logic.data.identity.orgName, orgName]);

    const displayName = logic.data.identity.orgName !== '—' ? logic.data.identity.orgName : orgName ?? 'Company';
    const failedSources = Object.keys(logic.errors);
    const disableDownload = logic.enabledModules.length === 0 || logic.status === 'loading' || downloadState === 'preparing';

    const handleDownload = async () => {
        if (disableDownload) return;
        setDownloadState('preparing');
        await new Promise((r) => setTimeout(r, 0));

        const renderToBlob = (overrides: PdfLayoutOverrides) => pdf(<ReportDocument {...docProps} layoutOverrides={overrides} />).toBlob();
        const rootErrOf = (e: unknown): Error => ((e as any)?.cause as Error) || (e instanceof Error ? e : new Error(String(e)));

        try {
            const blob = await renderToBlob(layoutOverrides);
            saveAs(blob, fileName);
            setDownloadState('idle');
            return;
        } catch (firstErr) {
            const rootErr = rootErrOf(firstErr);
            // If the user already explicitly picked A3, don't retry — log
            // diagnostic and stop. Otherwise fall back to A3 (bigger page +
            // fresh yoga context) which typically clears the scale-related
            // crashes that the default A4 layout can hit on dense orgs.
            if (layoutOverrides.paperSize === 'A3') {
                logPdfFailureDiagnostic(rootErr, logic.data, logic.enabledModules, layoutOverrides);
                setDownloadState('error');
                return;
            }
            console.warn(`[AicaReportV2] A4 render failed (${rootErr.message}); retrying on A3 fallback`);
            try {
                const a3Overrides: PdfLayoutOverrides = { ...layoutOverrides, paperSize: 'A3' };
                const blob = await renderToBlob(a3Overrides);
                saveAs(blob, fileName);
                setDownloadState('idle');
            } catch (a3Err) {
                const rootA3Err = rootErrOf(a3Err);
                console.error('[AicaReportV2] A3 fallback also failed — giving up.');
                logPdfFailureDiagnostic(rootA3Err, logic.data, logic.enabledModules, { ...layoutOverrides, paperSize: 'A3' });
                setDownloadState('error');
            }
        }
    };

    const downloadLabel =
        downloadState === 'preparing' ? 'Preparing PDF…' : downloadState === 'error' ? 'PDF error — retry' : 'Download PDF';

    return (
        <BottomDrawer
            onClose={onClose}
            height="100vh"
            allowClose
            showHeading={false}
            style={{ maxHeight: '100vh', height: '100vh', borderRadius: 0 }}
            iconStyle={{
                position: 'absolute',
                top: 28,
                right: 24,
                marginTop: 0,
                marginRight: 0,
                height: 15,
                width: 15,
                background: 'transparent',
                border: 'none',
                padding: 0,
                borderRadius: 0,
                boxShadow: 'none',
                zIndex: 10,
            }}
        >
            <div className={styles.Root}>
                <div className={styles.TopBar}>
                    <div className={styles.TitleBlock}>
                        <span className={styles.Title}>{displayName}</span>
                        <span className={styles.Sub}>
                            {logic.enabledModules.length} of {logic.modulesPresent.length} modules selected
                        </span>
                    </div>
                    <div className={styles.RightActions}>
                        {showLayoutPopover ? (
                            <LayoutSettingsPopover value={layoutOverrides} onChange={setLayoutOverrides} disabled={layoutPopoverDisabled} />
                        ) : null}
                        <button
                            type="button"
                            className={clsx(styles.Download, { [styles.Disabled]: disableDownload })}
                            disabled={disableDownload}
                            onClick={handleDownload}
                        >
                            {downloadLabel}
                        </button>
                    </div>
                </div>

                <div className={styles.Body}>
                    <ModuleRail
                        items={logic.railItems}
                        searchTerm={logic.searchTerm}
                        setSearchTerm={logic.setSearchTerm}
                        toggleModule={logic.toggleModule}
                        selectAll={logic.selectAll}
                        deselectAll={logic.deselectAll}
                        allEnabled={logic.allEnabled}
                        reorder={logic.reorder}
                        loading={logic.status !== 'ready'}
                    />

                    <section className={styles.PreviewCol}>
                        {logic.status === 'error' ? (
                            <div className={clsx(styles.Banner, styles.Error)}>
                                Every data source failed for <strong>{assesseeOrgId}</strong>.
                            </div>
                        ) : failedSources.length > 0 ? (
                            <div className={styles.Banner}>
                                Loaded with {failedSources.length} source{failedSources.length === 1 ? '' : 's'} missing:{' '}
                                {failedSources.join(', ')}
                            </div>
                        ) : null}

                        <div className={styles.PreviewCount}>
                            {logic.status === 'loading'
                                ? 'Fetching report data…'
                                : `Showing ${logic.enabledModules.length} of ${logic.modulesPresent.length} available modules`}
                        </div>

                        {logic.status === 'loading' ? (
                            <div className={styles.Empty}>Fetching report data…</div>
                        ) : logic.enabledModules.length === 0 ? (
                            <div className={styles.Empty}>No modules selected. Pick at least one from the left to preview & export.</div>
                        ) : (
                            <ReportPreview {...docProps} />
                        )}
                    </section>
                </div>
            </div>
        </BottomDrawer>
    );
};

export default AicaReportV2Drawer;
